// AppContextProvider.js - 통합 Context Provider (중첩 제거 및 최적화)
import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UserProvider } from './UserContext';
import { GlobalTimerProvider } from './GlobalTimerContext';
import { GlobalPointsProvider } from './GlobalPointsContext';
import { CartProvider } from './CartContext';

// 🚀 React Query 전역 설정 (최적화)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,              // 60초 캐시 (30초 → 60초로 증가)
      cacheTime: 600000,             // 10분 후 garbage collection (5분 → 10분으로 증가)
      refetchOnWindowFocus: true,    // 백그라운드 복귀 시 자동 갱신
      refetchOnReconnect: true,      // 네트워크 재연결 시 자동 갱신
      retry: 3,                      // 실패 시 3번 자동 재시도
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
    },
    mutations: {
      retry: (failureCount, error) => {
        // 🚨 500 에러는 재시도하지 않음 (큐 시스템이 자동 처리)
        if (error?.status === 500 || error?.message?.includes('500')) {
          return false;
        }
        // 기타 에러는 1번만 재시도
        return failureCount < 1;
      },
      retryDelay: 1000, // 1초 후 재시도
    }
  }
});

/**
 * 🚀 통합 Context Provider
 * - QueryClientProvider, UserProvider, GlobalTimerProvider, GlobalPointsProvider, CartProvider를 하나로 통합
 * - 중첩 구조를 단순화하여 성능 최적화
 * - Suspense로 lazy initialization 지원
 */
export const AppContextProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <Suspense fallback={null}>
          <GlobalTimerProvider>
            <GlobalPointsProvider>
              <CartProvider>
                {children}
              </CartProvider>
            </GlobalPointsProvider>
          </GlobalTimerProvider>
        </Suspense>
      </UserProvider>
    </QueryClientProvider>
  );
};

// QueryClient export (기존 코드 호환성)
export { queryClient };

